import React, { useContext } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import FieldError from "../../../shared/FieldError";
import Table from "../../../shared/Table";
import CprOrderRequest from "./CprOrderRequest";
import Lead from "../../../shared/Lead";
import * as API from "../../../api";
import * as CONST from "../../../helpers/Constants";
import { Settings } from "../../../helpers/Contexts";
import DataEntryNeeded from "../DataEntryNeeded";
import ResetFormButton from "../../forms/ResetFormButton";
import LastMeasurementForm from "../../forms/LastMeasurementForm";
import LastMeasuredQmsTable from "../LastMeasuredQmsTable";
import SendCprNotificationButton from "../SendCprNotificationButton";

const QualityMeasureStatusCheck = ({ task }) => {
  const settings = useContext(Settings);
  const [localTask, setLocalTask] = React.useState(task);

  // Keep in sync if parent sends a different task
  React.useEffect(() => {
    setLocalTask(task);
  }, [task]);

  const initialPromptVal = React.useMemo(() => {
    const val = localTask?.latest_quality_measurement;
    if (val === null || val === undefined) return null;
    return val === true ? "1" : "2";
  }, [localTask]);

  const [promptVal, setPromptVal] = React.useState(initialPromptVal);

  React.useEffect(() => {
    setPromptVal(initialPromptVal);
  }, [initialPromptVal]);

  const [submit, setSubmit] = React.useState(false);
  let [patientNotified, setPatientNotified] = React.useState(false);
  let [showDataEntry, setShowDataEntry] = React.useState(
    localTask.cpr.status == 'closed' &&
    localTask.cpr.procedure_type == 'Annual Wellness Visit'
  );

  React.useEffect(() => {
    setShowDataEntry(
      localTask.cpr.status == "closed" &&
        localTask.cpr.procedure_type == "Annual Wellness Visit"
    );
  }, [localTask]);

  const handlePromptChange = async (e) => {
    const value = e.target.value;
    setPromptVal(value);

    const latestQualityMeasurementValue = value == "1"; // or include "2" if you want both Yes/No

    if (latestQualityMeasurementValue) {
      const response = await API.updateTask(localTask.id, {
        task: {
          id: localTask.id,
          reset_order_document_verified: true,
        },
      }).then((response) => {
        // Update the local task state so CprOrderRequest re-renders
        setLocalTask((prev) => ({
          ...prev,
          cpr: {
            ...prev.cpr,
            order_document_verified: null,
          },
        }));
      }).catch((err) => {
        console.error("Failed to update task:", err);
      });
    }
  };

  const isTaskClosed = localTask.status === "closed";
  const isAWV = localTask.cpr.procedure_type === "Annual Wellness Visit";
  const isCCS = localTask.cpr.procedure_type === "Colon Cancer Screening";
  const hasLastMeasurements =
    (localTask.cpr.last_measured_quality_measurements || []).length > 0;

  return (
    <div className="p1">
      <Lead message={localTask.label} />
      <br />
      <div className="pt2s">
        {CONST.QM_STATUS_CHECK.BODY_1}
        {isCCS ? (
          <>
             {CONST.QM_STATUS_CHECK.CCS_TEXT}
            <b> {localTask.cpr.procedure_type}(s) </b>
            {CONST.QM_STATUS_CHECK.CCS_COMPLETED_DATE_TEXT}
            <div className="pt pb2">
              {!!localTask.cpr.last_measured_quality_measurements.length > 0 ? (
                <LastMeasuredQmsTable task={localTask} />
              ) : (
                <b>NO RECORDS FOUND</b>
              )}
            </div>
          </>
        ) : (
          <>
             {CONST.QM_STATUS_CHECK.MEASUREMENT_TYPE_TEXT}
            <b> {localTask.cpr.procedure_type} </b>
            {CONST.QM_STATUS_CHECK.COMPLETED_DATE_TEXT}:
            <div className="pt pb2">
              {!!localTask.cpr.last_measured_quality_measurements.length > 0 ? (
                <LastMeasuredQmsTable task={localTask} />
              ) : (
                <b>NO RECORD FOUND</b>
              )}
            </div>
          </>
        )}
      </div>

      {isTaskClosed && localTask.cpr.order_not_available_reason && localTask.cpr.order_not_available_reason_verified && (
        <>
          <label>Reason (Order Not Available) Verified <i className='fa fa-check-circle green-text'></i> </label>
          <label>Reason:</label>{ localTask.cpr.order_not_available_reason}
        </>
      )}

      {!isTaskClosed && (
        <div className="pt2">
          <p>{CONST.QM_STATUS_CHECK.BODY_2}</p>
          <b>{CONST.QM_STATUS_CHECK.FORM.PROMPT}</b>
          <br />
          <select
            id="cpr-status-check"
            className="cpr-form"
            value={promptVal}
            disabled={localTask.status == 'closed'}
            placeholder="Select an option"
            onChange={handlePromptChange}
          >
            {!promptVal && <option>Select an option</option>}
            {CONST.QM_STATUS_CHECK.FORM.OPTIONS.map((i) => (
              <option value={i.value} key={i.value}>
                {i.label}
              </option>
            ))}
          </select>

          <div className="pt">
            {promptVal == "1" && isAWV && (
              <>
                <b>You can close this task.</b>
              </>
            )}
            {promptVal == "2" && !showDataEntry && (
              <>
                <b>{CONST.QM_STATUS_CHECK.SELECTED_VALUE_2} </b>
                <LastMeasurementForm showDataEntry={showDataEntry} setShowDataEntry={setShowDataEntry} task={task}/>
              </>
            )}
          </div>
        </div>
      )}

      {promptVal == "1" && !isAWV &&
        (
        <CprOrderRequest
          task={{
            ...localTask,
          }}
        />
      )}

      { showDataEntry == true && (
          <DataEntryNeeded
            task={{ ...localTask }}
          />
        )
      }
    </div>
  );
};

export default QualityMeasureStatusCheck;
